import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { Input, Textarea, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { AppMessage } from "../api/types";

/**
 * Round 35 items 7/8/12 — System Messages and Activity Messages. Two
 * fully independent admin sections (separate routes/tables — see
 * routes/admin/messages.js's own doc) that happen to render with one
 * shared component here purely because the shape and behavior are
 * identical, the same "shared implementation, distinct entry points"
 * precedent CoinProductsPage/NotificationsPage's own generic-CRUD shape
 * already follows for this codebase — SystemMessagesPage and
 * ActivityMessagesPage below are the two real, separately-routed exports
 * (see App.tsx/Sidebar.tsx), never a merged "Notifications v2" page.
 */
function MessagesPage({ apiPath, kind }: { apiPath: string; kind: "System" | "Activity" }) {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<AppMessage>(apiPath);
  const [editing, setEditing] = useState<Partial<AppMessage> | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AppMessage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showToast } = useToast();

  async function toggleActive(row: AppMessage) {
    try {
      await apiRequest(`${apiPath}/${row.id}`, { method: "PATCH", body: { is_active: !row.is_active } });
      showToast(row.is_active ? "Disabled" : "Enabled", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update message", "error");
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await apiRequest(`${apiPath}/${deleteTarget.id}`, { method: "DELETE" });
      showToast(`${kind} message deleted`, "success");
      setDeleteTarget(null);
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete", "error");
    } finally {
      setIsDeleting(false);
    }
  }

  async function save() {
    if (!editing?.title?.trim() || !editing?.description?.trim()) {
      showToast("Title and description are required", "error");
      return;
    }
    const sendToAll = editing.send_to_all !== false; // default to All users for a fresh compose
    if (!sendToAll && !editing.recipient_user_id?.trim()) {
      showToast("Pick a recipient user id, or switch Send to: All users", "error");
      return;
    }
    if (editing.link?.trim() && editing.open_profile_user_id?.trim()) {
      showToast("Link and User ID are alternatives — clear one of them", "error");
      return;
    }

    const body = {
      title: editing.title.trim(),
      description: editing.description.trim(),
      image_url: editing.image_url?.trim() || null,
      link: editing.link?.trim() || null,
      open_profile_user_id: editing.open_profile_user_id?.trim() || null,
      recipient_user_id: editing.recipient_user_id?.trim() || null,
      send_to_all: sendToAll,
    };

    setIsSubmitting(true);
    try {
      if (isNew) {
        await apiRequest(apiPath, { method: "POST", body });
      } else {
        await apiRequest(`${apiPath}/${editingId}`, { method: "PATCH", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  const columns: Column<AppMessage>[] = [
    {
      header: "Title",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.image_url && <img src={row.image_url} alt="" className="h-8 w-8 rounded-md object-cover" />}
          <span className="font-medium">{row.title}</span>
        </div>
      ),
    },
    { header: "Description", render: (row) => <span className="max-w-xs truncate text-ink-muted">{row.description}</span> },
    {
      header: "Target",
      render: (row) =>
        row.send_to_all ? (
          <Badge tone="gold">All users</Badge>
        ) : (
          <span className="font-mono text-xs">{row.recipient_username ?? row.recipient_public_user_id ?? row.recipient_user_id ?? "—"}</span>
        ),
    },
    { header: "Created", render: (row) => new Date(row.created_at).toLocaleString() },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Disabled</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setEditing(row);
              setEditingId(row.id);
              setIsNew(false);
            }}
          >
            Edit
          </Button>
          <Button size="sm" variant="secondary" onClick={() => toggleActive(row)}>
            {row.is_active ? "Disable" : "Enable"}
          </Button>
          <Button size="sm" variant="danger" onClick={() => setDeleteTarget(row)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title={`${kind} Messages`}>
      <div className="mb-4 flex justify-between">
        <p className="max-w-lg text-xs text-ink-faint">
          Link and User ID are alternatives — set at most one, never both. With neither, tapping the message in the app does nothing.
        </p>
        <Button
          onClick={() => {
            setEditing({ send_to_all: true });
            setEditingId(null);
            setIsNew(true);
          }}
        >
          + Create {kind} Message
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle={`No ${kind.toLowerCase()} messages yet`}
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={isNew ? `Create ${kind} Message` : `Edit ${kind} Message`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Title" value={editing?.title ?? ""} onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))} />
          <Input
            label="Image URL (optional)"
            value={editing?.image_url ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, image_url: e.target.value }))}
          />
          <Textarea
            label="Description"
            rows={3}
            value={editing?.description ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))}
          />
          <Select
            label="Send to"
            value={editing?.send_to_all === false ? "one" : "all"}
            onChange={(e) => setEditing((p) => ({ ...p, send_to_all: e.target.value === "all" }))}
          >
            <option value="all">All users</option>
            <option value="one">One specific user</option>
          </Select>
          {editing?.send_to_all === false && (
            <Input
              label="Recipient (for Send to: one user)"
              hint="Who receives this message. Independent of Link/User ID below — this message can still carry a Link or its own tap-target User ID."
              value={editing?.recipient_user_id ?? ""}
              onChange={(e) => setEditing((p) => ({ ...p, recipient_user_id: e.target.value }))}
            />
          )}

          <div className="rounded-lg border border-surface-border p-3">
            <p className="mb-2 text-xs font-medium text-ink-muted">Tap behavior — Link and User ID are alternatives (set at most one)</p>
            <div className="space-y-3">
              <Input
                label="Link (opens in-app when tapped)"
                value={editing?.link ?? ""}
                onChange={(e) => setEditing((p) => ({ ...p, link: e.target.value }))}
              />
              <Input
                label="User ID (opens that user's Profile Info when tapped)"
                hint="Independent of Recipient above — this can point at a different user entirely, or be left empty."
                value={editing?.open_profile_user_id ?? ""}
                onChange={(e) => setEditing((p) => ({ ...p, open_profile_user_id: e.target.value }))}
              />
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        isDangerous
        title={`Delete ${kind.toLowerCase()} message?`}
        confirmLabel="Delete"
        description={
          deleteTarget
            ? `"${deleteTarget.title}" will be permanently removed. If it's already been read by a recipient this will be blocked — disable it instead.`
            : ""
        }
      />
    </AppShell>
  );
}

export function SystemMessagesPage() {
  return <MessagesPage apiPath="/admin/system-messages" kind="System" />;
}

export function ActivityMessagesPage() {
  return <MessagesPage apiPath="/admin/activity-messages" kind="Activity" />;
}
